import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import L from 'leaflet';
import Settings from 'settings.json';
import type { GridParams } from 'contexts/GridContext';
import CadastralMapSection, { ReadOnlyAnalysisAreaCenterMarkerSync } from 'views/add-grid/map/CadastralMapSection';
import MapPreviewModeFrame from 'views/add-grid/map/MapPreviewModeFrame';
import Style from 'views/add-grid/styles/grid.tailwind';
import { crs2056, INITIAL_GRID_MAP_VIEWS, type GridMapType } from 'views/add-grid/map/mapConfig';
import { defaultCoordinateSystemPreference } from 'utils/coordinateSystemManager';

export type ProjectCreationPreviewState = {
  gridId?: string | number | null;
  isPreviewLoading?: boolean;
  onUpdatePreview?: (() => void | Promise<void>) | null;
  hasUnsavedChanges?: boolean;
  hasValidationErrors?: boolean;
  mapCenter: [number, number];
  gridParams: GridParams;
};

type ProjectCreationPersistentPreviewProps = {
  preview: ProjectCreationPreviewState;
  visible?: boolean;
  style?: CSSProperties;
};

const ProjectCreationPersistentPreview = ({
  preview,
  visible = true,
  style,
}: ProjectCreationPersistentPreviewProps) => {
  const [mapType, setMapType] = useState<GridMapType>('cadastral');
  const [mapViews, setMapViews] = useState(INITIAL_GRID_MAP_VIEWS);
  const legalBorderRef = useRef<L.Polygon | null>(null);
  const buildableBorderRef = useRef<L.Polygon | null>(null);
  const analysisAreaRef = useRef<L.Polygon | null>(null);
  const analysisAreaInactiveRefs = useRef<L.Polygon[]>([]);
  const isDrawingRef = useRef(false);
  const hasCenteredRef = useRef(false);
  const [stableMapCenter, setStableMapCenter] = useState<[number, number] | null>(null);
  const hasMapCenter = preview.mapCenter[0] !== 0 && preview.mapCenter[1] !== 0;

  useEffect(() => {
    if (!hasMapCenter || hasCenteredRef.current) {
      return;
    }

    hasCenteredRef.current = true;
    setStableMapCenter(preview.mapCenter);
    setMapViews((prev) => ({
      cadastral: { ...prev.cadastral, center: preview.mapCenter },
      osm: { ...prev.osm, center: preview.mapCenter },
    }));
  }, [hasMapCenter, preview.mapCenter]);

  const shellStyle = useMemo<CSSProperties>(
    () => ({
      position: 'absolute',
      zIndex: 20,
      display: visible ? 'block' : 'none',
      pointerEvents: visible ? 'auto' : 'none',
      opacity: visible ? 1 : 0,
      transition: 'opacity 120ms ease',
      ...style,
    }),
    [style, visible]
  );

  return (
    <div style={shellStyle}>
      <MapPreviewModeFrame
        gridId={preview.gridId}
        isPreviewLoading={preview.isPreviewLoading}
        onUpdatePreview={preview.onUpdatePreview}
        hasUnsavedChanges={preview.hasUnsavedChanges}
        hasValidationErrors={preview.hasValidationErrors}
      >
        <CadastralMapSection
          isReview={false}
          readOnly={true}
          allowAnalysisAreaAngleCopy={true}
          isParcelInteractionDisabled={true}
          mapType={mapType}
          mapViews={mapViews}
          setMapType={setMapType}
          setMapViews={setMapViews}
          mapCenter={stableMapCenter ?? preview.mapCenter}
          crs2056={crs2056}
          gridParams={preview.gridParams}
          Style={Style}
          legalBorderRef={legalBorderRef}
          buildableBorderRef={buildableBorderRef}
          analysisAreaRef={analysisAreaRef}
          analysisAreaInactiveRefs={analysisAreaInactiveRefs}
          showOnlyAnalysisAreas
          inactiveAnalysisAreaStyle={Style.analysisArea}
          activeAnalysisAreaIndex={preview.gridParams.activeAnalysisAreaIndex}
          isDrawingRef={isDrawingRef}
          showEditableCoordinatesArea={false}
          AnalysisAreaCenterMarkerSync={ReadOnlyAnalysisAreaCenterMarkerSync}
          cadastralOpacity={Number(Settings[import.meta.env.MODE as keyof typeof Settings].cadastral_opacity)}
          focusedPolygonRef={null}
          coordinateSystem={defaultCoordinateSystemPreference}
          cadastralLayerKey={0}
        />
      </MapPreviewModeFrame>
    </div>
  );
};

export default ProjectCreationPersistentPreview;
